// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import OrderConfirmationBillingInformation, {
    OrderConfirmationBillingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import OrderConfirmationShippingInformation, {
    OrderConfirmationShippingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { checkoutSharedStyles } from "@tps/App/SharedStyles/CheckoutSharedStyles";
import OrderConfirmationPaymentInformation from "@tps/App/Widgets/OrderConfirmation/OrderConfirmationPaymentInformation";
import { parseBoolean } from "@tps/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
};

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    shippingInformationGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberTitleText?: TypographyPresentationProps;
    vatNumberText?: TypographyPresentationProps;
    orderConfirmationShippingInformation?: OrderConfirmationShippingInformationStyles;
    billingInformationGridItem?: GridItemProps;
    orderConfirmationBillingInformation?: OrderConfirmationBillingInformationStyles;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    // XNG-Change
    orderInformationWrapper?: InjectableCss;
    orderDetailsWrapper?: InjectableCss;
    billingInformationWrapper?: InjectableCss;
    billingShippingWrapper?: InjectableCss;
    requiresMtr?: TypographyPresentationProps;
    dateRequestedTitle?: TypographyPresentationProps;
    dateRequestedText?: TypographyPresentationProps;
    requestedDateWrapper?: InjectableCss;
    detailsWrapper?: InjectableCss;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        gap: 10,
    },
    orderNumberGridItem: {
        width: 12,
    },
    orderNumberHeading: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shippingInformationGridItem: {
        width: 12,
    },
    vatNumberGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    vatNumberTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billingInformationGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    orderInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    orderDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderDetailsGridContainer: {
        css: css`
            padding-left: 5px;
            padding-bottom: 15px;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation = ({
    cartState,
    billToState,
    shipToState,
    orderState,
    pickUpWarehouse,
    enableVat,
    language,
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
}: Props) => {
    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }

        if (!orderState.isLoading && !orderState.value && cart.orderNumber && cart.status === "Submitted") {
            loadOrderByOrderNumber({ orderNumber: cart.orderNumber });
        }
    }, [cartState]);

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    const order = orderState.value;
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    return (
        <StyledWrapper {...styles.orderInformationWrapper}>
            <StyledWrapper {...styles.orderDetailsWrapper}>
                <Typography {...checkoutSharedStyles.sectionTitle}>{translate("Addresses")}</Typography>

                <StyledWrapper {...styles.billingShippingWrapper}>
                    <StyledWrapper {...styles.billingInformationWrapper}>
                        <OrderConfirmationBillingInformation
                            cart={cart}
                            billTo={billTo}
                            extendedStyles={styles.orderConfirmationBillingInformation}
                        />
                        {(cart.properties.isRequireMtr ||
                            cart.notes ||
                            cart.requestedDeliveryDate ||
                            cart.requestedPickupDate) && (
                            <>
                                <StyledWrapper {...checkoutSharedStyles.billingInfoAndOrderDetailsSeparator} />
                                <StyledWrapper>
                                    <Typography {...checkoutSharedStyles.subSectionTitle}>
                                        {translate("Order Details")}
                                    </Typography>
                                    <StyledWrapper {...styles.detailsWrapper}>
                                        {cart.properties.isRequireMtr && (
                                            <Typography
                                                className={cart.notes ? "requireMargin" : ""}
                                                {...styles.requiresMtr}
                                            >
                                                {/* XNG-Changes */}                                                
                                                {translate("This Order Requires MTR = ")}
                                                {parseBoolean(cart.properties.isRequireMtr)
                                                    ? translate("Yes")
                                                    : translate("No")}
                                            </Typography>
                                        )}
                                        {cart.notes && (
                                            <StyledWrapper {...styles.notesGridItem}>
                                                <Typography {...styles.notesTitle}>
                                                    {translate("Order Notes")}
                                                </Typography>
                                                <Typography {...styles.notesDescription}>{cart.notes}</Typography>
                                            </StyledWrapper>
                                        )}
                                        {(cart.requestedDeliveryDate || cart.requestedPickupDate) && (
                                            <StyledWrapper {...styles.requestedDateWrapper}>
                                                <Typography {...styles.dateRequestedTitle}>
                                                    {translate("Date Requested")}
                                                </Typography>
                                                <Typography
                                                    {...styles.dateRequestedText}
                                                    data-test-selector="orderConfirmation_requestedDate"
                                                >
                                                    <LocalizedDateTime
                                                        dateTime={
                                                            cart.requestedDeliveryDateDisplay ||
                                                            cart.requestedPickupDateDisplay
                                                        }
                                                    />
                                                </Typography>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.shippingInformationGridItem}>
                        <OrderConfirmationShippingInformation
                            cart={cart}
                            shipTo={shipTo}
                            pickUpWarehouse={pickUpWarehouse}
                            extendedStyles={styles.orderConfirmationShippingInformation}
                        />
                    </StyledWrapper>
                </StyledWrapper>

                {/* {enableVat && cart.customerVatNumber && (
                    <GridItem {...styles.vatNumberGridItem}>
                        <Typography {...styles.vatNumberTitleText} as="h3">
                            {translate("VAT Number")}
                        </Typography>
                        <Typography {...styles.vatNumberText}>{cart.customerVatNumber}</Typography>
                    </GridItem>
                )} */}
            </StyledWrapper>
            <StyledWrapper {...checkoutSharedStyles.separator} />
            <OrderConfirmationPaymentInformation cart={cart} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationOrderInformation),
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
    },
};

export default widgetModule;
